import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import { SvgIcon } from '@mui/material';
import { DS_palette as corePalette } from 'mui/base/theme/DS_Tokens/core';
import { ReactComponent as PinIcon } from 'mui/assets/icons/place-pin.svg';
import Button from 'mui/components/elements/DS_ButtonLink';
import Markdown from 'mui/components/elements/DS_Markdown';
import RichText from 'mui/components/elements/RichText';

import Select from 'mui/components/elements/Form/Select';

import { ReactHookTextarea } from 'components/Form';
import { Checkbox, ReactHookInput, ReactHookPhoneInput } from 'mui/components/elements/Form';

import { getFormMessages, getValidFormData } from 'components/Booking-new/utils/steps/bookingform';
import { loadFromLocalStorage } from '../../../../utils/helpers/loadFromLocalStorage';

import { setUserSelectedCity } from '../../../../actions';
import { useDispatch } from '../../../../hooks';
import schema from './schema';

import {
  FormBorderBox,
  RichTextWrapper,
  StyledForm,
  StyledButtonWrapper,
  StyledLoaderSpinner,
  StyledMarketingDisclaimer,
  StyledChosenLocation,
} from './styles';

const Form = ({
  formData,
  personalData,
  onFormSubmit,
  selectedCityValue,
  stepLocationDataCity,
  stepLocationDataClinic,
  cityOptions = [],
}) => {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const formMessages = getFormMessages({ data: formData });
  const localUserData = loadFromLocalStorage('submitData');

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    setValue,
    trigger,
  } = useForm({
    mode: 'onChange',
    context: {
      enableAdditionalInfo: formData?.enableAdditionalInfo,
      enableCitiesDropdownList: formData?.enableCitiesDropdownList,
    },
    resolver: yupResolver(schema(formMessages, selectedCountry)),
  });

  const onSubmit = async submitData => {
    const validData = getValidFormData(submitData);
    await onFormSubmit(validData);
  };

  useEffect(() => {
    const initialCityValue = selectedCityValue || stepLocationDataCity || '';
    if (initialCityValue) {
      setValue('city', initialCityValue); // set the initial value
      trigger('city'); // trigger validation for the city field
    }
  }, []);

  const dispatch = useDispatch();

  return (
    <StyledForm data-test="styledForm" onSubmit={handleSubmit(onSubmit)}>
      <FormBorderBox data-test="FormBorderBox" hasBorder={formData?.hasBorder}>
        <ReactHookInput
          {...register('firstName')}
          errors={errors}
          defaultValue={personalData?.firstName || localUserData?.firstName || ''}
          placeholder={`${formData?.firstNameLabel || 'First Name'}*`}
        />
        <ReactHookInput
          {...register('lastName')}
          errors={errors}
          defaultValue={personalData?.lastName || localUserData?.lastName || ''}
          placeholder={`${formData?.lastNameLabel || 'Last Name'}*`}
        />
        <ReactHookInput
          {...register('email')}
          errors={errors}
          type="email"
          defaultValue={personalData?.email || localUserData?.email || ''}
          placeholder={`${formData?.email || 'Email'}*`}
        />
        <ReactHookPhoneInput
          errors={errors}
          name="phoneNumber"
          defaultValue={personalData?.phoneNumber || localUserData?.phoneNumber || ''}
          setValue={setValue}
          control={control}
          placeholder={`${formData?.phoneNumber || 'Phone Number'}*`}
          setSelectedCountry={setSelectedCountry}
        />

        {formData?.enableCitiesDropdownList ? (
          <Controller
            name="city"
            control={control}
            rules={{ required: formData?.enableCitiesDropdownList }}
            render={({ field: { onChange, value } }) => (
              <>
                <Select
                  errors={errors}
                  value={value || selectedCityValue || stepLocationDataCity || ''}
                  onSelect={selectedValue => {
                    dispatch(setUserSelectedCity(selectedValue));

                    onChange(selectedValue.value); // Ensure this updates the form state
                    setValue('city', selectedValue.value); // Explicitly set the value
                    trigger();
                  }}
                  placeholder={formData?.cityLabel || 'Select city'}
                  options={cityOptions}
                  errorMessage={errors.city?.message}
                  cityError={formData?.cityLabelError}
                  trigger={trigger}
                />
                {stepLocationDataClinic && (
                  <StyledChosenLocation>
                    <span>
                      {' '}
                      <SvgIcon component={PinIcon} inheritViewBox={true} sx={{ fontSize: 24 }} />
                    </span>
                    {stepLocationDataClinic}
                  </StyledChosenLocation>
                )}
              </>
            )}
          />
        ) : null}
        {formData?.enableAdditionalInfo ? (
          <ReactHookTextarea
            {...register('additionalInfo')}
            errors={errors}
            placeholder={`${formData?.additionalInfoLabel || 'Additional information'}*`}
            defaultValue={personalData?.additionalInfo || ''}
          />
        ) : null}
        {formData?.ageConsentLabel ? (
          <Checkbox
            {...register('ageConsent')}
            errors={errors}
            label={`${formData?.ageConsentLabel}*`}
            defaultChecked={personalData?.ageConsent || false}
          />
        ) : null}
        {(formData.enableMarketingConsentLabel && formData.marketingConsentLabel) ||
        (formData.enableMarketingDisclaimer && formData.marketingConsentDisclaimer.markdown.html) ? (
          <Checkbox
            {...register('marketingConsent')}
            errors={errors}
            label={
              <StyledMarketingDisclaimer>
                {formData.enableMarketingConsentLabel && formData.marketingConsentLabel ? (
                  <p>{formData.marketingConsentLabel}</p>
                ) : null}
                {formData.enableMarketingDisclaimer && formData.marketingConsentDisclaimer.markdown.html ? (
                  <Markdown>{formData.marketingConsentDisclaimer.markdown.html}</Markdown>
                ) : null}
              </StyledMarketingDisclaimer>
            }
            defaultChecked={personalData?.marketingConsent || false}
          />
        ) : null}
        <StyledButtonWrapper data-test="booking-finalize-confirm-button">
          {isSubmitting ? (
            <StyledLoaderSpinner color={corePalette.blue[500]} />
          ) : (
            <Button
              id="bookSubmit"
              type="submit"
              aria-label="Submit booking"
              disabled={!isValid || isSubmitting}
              title={formData?.bookButtonLabel || 'Book'}
              buttonSize={false}
            />
          )}
        </StyledButtonWrapper>
      </FormBorderBox>
      <RichTextWrapper>{formData?.userConsent ? <RichText document={formData.userConsent} /> : null}</RichTextWrapper>
    </StyledForm>
  );
};

Form.propTypes = {
  formData: PropTypes.shape({
    ageConsentLabel: PropTypes.string,
    marketingConsentLabel: PropTypes.string,
    marketingConsentDisclaimer: PropTypes.object,
    bookButtonLabel: PropTypes.string,
    email: PropTypes.string,
    firstNameLabel: PropTypes.string,
    hasBorder: PropTypes.bool,
    lastNameLabel: PropTypes.string,
    phoneNumber: PropTypes.string,
    userConsent: PropTypes.object,
  }),
  personalData: PropTypes.object,
  onFormSubmit: PropTypes.func,
};

export default Form;
